import { Injectable, signal } from '@angular/core';
import { IExplorerNavAttributes } from '@optima-stride-ai/optima-components';
/**
 * NavigationStateService
 * 
 * Maintains a consistent navigation state across all viewer components.
 * This ensures that the left navigation panel (explorerNavAttributes) 
 * remains consistent regardless of which viewer is currently active.
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationStateService {
  
  // Store the original navigation attributes from the library page
  private _originalNavAttributes = signal<IExplorerNavAttributes | null>(null);
  
  // Public readonly access to the navigation state
  readonly originalNavAttributes = this._originalNavAttributes.asReadonly();

  /**
   * Set the original navigation attributes from the library page.
   * This should be called from the library page when initializing or updating navigation.
   * 
   * @param navAttributes - The navigation attributes from the library page
   */
  setOriginalNavAttributes(navAttributes: IExplorerNavAttributes): void {
    this._originalNavAttributes.set({
      ...navAttributes
    });
  }

  /**
   * Get a copy of the original navigation attributes.
   * This ensures each viewer gets a fresh copy without modifying the original.
   * 
   * @returns A copy of the original navigation attributes or a default structure
   */
  getNavAttributesForViewer(): IExplorerNavAttributes {
    const original = this._originalNavAttributes();
    
    if (!original) {
      return {
        data: [],
        currentSelectedItem: '',
        isNavigable: false
      };
    }

    // Return a deep copy to prevent accidental mutations
    return {
      data: [...(original.data || [])],
      currentSelectedItem: original.currentSelectedItem || '',
      isNavigable: original.isNavigable || false,
      // Copy any other properties if they exist
      ...(original as any)
    };
  }

  /**
   * Update the current selected item in the navigation.
   * This allows viewers to update which item is highlighted in navigation
   * without affecting the overall navigation structure.
   * 
   * @param itemId - The ID of the item to select
   */
  updateCurrentSelectedItem(itemId: string): void {
    const current = this._originalNavAttributes();
    if (current) {
      this._originalNavAttributes.set({
        ...current,
        currentSelectedItem: itemId
      });
    }
  }

  /**
   * Get the current selected item ID
   * 
   * @returns The current selected item ID or null if not set
   */
  getCurrentSelectedItem(): string | null {
    const current = this._originalNavAttributes();
    return current?.currentSelectedItem || null;
  }

  /**
   * Clear the navigation state (useful when leaving the library context)
   */
  clearNavigation(): void {
    this._originalNavAttributes.set(null);
  }

  /**
   * Check if navigation state is available
   * 
   * @returns true if navigation state exists, false otherwise
   */
  hasNavigationState(): boolean {
    return this._originalNavAttributes() !== null;
  }
}